/**
 * Blocklist error classification. The backend performs DNS-based domain
 * blocklist lookups (Spamhaus DBL / SURBL) when inviting a team member and, on a
 * listing, returns a 422 with the lookup message. The invite form surfaces that
 * raw detail to the user only when it's a genuine block — this decides that.
 * Ported from Ember's `utils/blocklist` + `utils/spamhaus`.
 */

/** A Spamhaus DBL listing return code (127.0.1.x) — the domain IS on the blocklist. */
const SPAMHAUS_BLOCK_RETURN_CODE = /return code:\s*127\.\d+\.\d+\.\d+/i;

/** Spamhaus DNS was unreachable / errored (rcode 2 = SERVFAIL) — a "fail open" case, not a block. */
const SPAMHAUS_UNAVAILABLE_PATTERNS = [/rcode[:\s]*2\b/i, /servfail/i, /return code:\s*2\b/i];

/** Whether the message mentions Spamhaus at all — a quick pre-filter. */
export function isSpamhausError(error: string): boolean {
  return error.toLowerCase().includes('spamhaus');
}

/** Whether Spamhaus was unreachable (DNS SERVFAIL / rcode 2), which we treat as not-a-block. */
function isSpamhausUnavailable(error: string): boolean {
  return SPAMHAUS_UNAVAILABLE_PATTERNS.some((pattern) => pattern.test(error));
}

/**
 * Whether the message indicates the domain is legitimately listed on the Spamhaus DBL. A 127.x.x.x
 * return code is a definite block; a Spamhaus message that matches neither the block nor the
 * unavailable pattern is treated as a block too, so a backend message-format change can't silently
 * let a blocked domain through.
 */
export function isSpamhausBlock(error: string): boolean {
  if (SPAMHAUS_BLOCK_RETURN_CODE.test(error)) {
    return true;
  }
  return isSpamhausError(error) && !isSpamhausUnavailable(error);
}

/**
 * Whether the error message indicates the email's domain was rejected because it is listed on a
 * domain blocklist (Spamhaus DBL or SURBL). The gate for surfacing the API detail directly.
 */
export function isBlocklistBlock(error: string): boolean {
  if (isSpamhausBlock(error)) {
    return true;
  }
  return /\bsurbl\b/i.test(error);
}
